import React from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Grid from '@mui/material/Grid'
import Text from '../../../../components/Text'
import { colors } from '../../../../styles/_var'
import ScoreIcon from '@mui/icons-material/EmojiEventsRounded'
import { Scoreboard } from '../../../../utils/types'
import { humanize, slugify } from '../../../../utils/helpers'

type ScoreboardListProps = {
  scoreboards: Scoreboard[]
  onScoreboardClick: (id: number, slug: string) => void
}

export const ScoreboardList = ({ scoreboards, onScoreboardClick }: ScoreboardListProps) => {
  return (
    <Grid container spacing={2}>
      {scoreboards.map((scoreboard) => (
        <Grid item xs={12} sm={6} md={4} key={scoreboard.id}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.02)',
                boxShadow: 4,
              },
            }}
            onClick={() => onScoreboardClick(scoreboard.id, slugify(scoreboard.title))}
          >
            <Box display="flex" alignItems="center" mb={1} sx={{ minWidth: 0 }}>
              <ScoreIcon sx={{ mr: 1, color: colors.primary, flexShrink: 0 }} />
              <Box
                sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}
                title={scoreboard.title}
              >
                <Text variant="medium" weight="bold" useDiv nowrap>
                  {scoreboard.title}
                </Text>
              </Box>
            </Box>
            {scoreboard.description && (
              <Text variant="regular" color={colors.darkgrey} useDiv>
                {scoreboard.description}
              </Text>
            )}
            {scoreboard.created_at && (
              <Box display="flex" justifyContent="flex-end" alignItems="center" mt={1}>
                <Text variant="small" color={colors.darkgrey} useDiv>
                  {humanize(scoreboard.created_at, true)}
                </Text>
              </Box>
            )}
          </Paper>
        </Grid>
      ))}
    </Grid>
  )
}

type ScoreboardLoadingStateProps = {
  count?: number
}

export const ScoreboardLoadingState = ({ count = 3 }: ScoreboardLoadingStateProps) => {
  return (
    <Grid container spacing={2}>
      {Array(count).fill(0).map((_, i) => (
        <Grid item xs={12} sm={6} md={4} key={i}>
          <Skeleton variant="rectangular" height={150} />
        </Grid>
      ))}
    </Grid>
  )
}

export const ScoreboardEmptyState = () => {
  return (
    <Paper
      elevation={2}
      sx={{
        p: 4,
        textAlign: 'center',
        border: `dashed 1px ${colors.darkgrey}`,
      }}
    >
      <ScoreIcon sx={{ fontSize: 48, color: colors.darkgrey, mb: 2 }} />
      <Text variant="medium" weight="semibold" useDiv>
        No Scoreboards yet
      </Text>
      <Text color={colors.darkgrey} mt={1} useDiv>
        Create your first Scoreboard to start tracking scores
      </Text>
    </Paper>
  )
}
